import { Button, type ButtonVariants } from "@/components/ui/baseUi/button"
import { StripeAccountStatus } from "@/modules/payment/stripeTypes"
import { useUser } from "@/modules/users/hooks/useUser"
import { useStripeAccount } from "@modules/finances/hooks/useStripeAccount"
import { getPaymentErrorButtonLabel } from "@modules/finances/hooks/useStripeAccountErrors"
import { type PaymentError, StripeAccountErrorCode, StripeErrorCode } from "@modules/finances/types/stripeErrorTypes"
import { getStripeAccountErrorText } from "@modules/finances/utils/financeUtils"
import { useBoxNavigate } from "@modules/navigation/hooks/useBoxNavigate"
import { Path } from "@modules/navigation/routes"
import { useProgramBundle } from "@modules/programBundle/hooks/useProgramBundle"
import { useProgramBundleAthleteSubscription } from "@modules/programBundle/hooks/useProgramBundleSubscription"
import { useProgramAthleteSubscription } from "@modules/programs/hooks/useProgramSubscription"
import { LoaderCircle } from "lucide-react"

export function StripeErrorProgramCardSettingsButton({
  programId,
  paymentError,
  buttonSize,
}: {
  programId: string
  paymentError: PaymentError
  buttonSize?: ButtonVariants["size"]
}) {
  const {
    openPaymentSettings,
    tryNewPayment,
    programStripeSubscriptionInfos,
    isUpdating: isUpdatingSubscription,
  } = useProgramAthleteSubscription(programId)

  const userSubscriptionError = programStripeSubscriptionInfos?.payment?.error
  const buttonLabel = getPaymentErrorButtonLabel(paymentError)

  return (
    <Button
      variant="primary"
      size={buttonSize ?? "md"}
      className="w-full"
      disabled={isUpdatingSubscription}
      onClick={() => {
        if (userSubscriptionError?.errorCode === StripeErrorCode.AUTHENTICATION_REQUIRED) tryNewPayment()
        else openPaymentSettings()
      }}
      isLoading={isUpdatingSubscription}
    >
      {isUpdatingSubscription && <LoaderCircle className="animate-spin" />}
      {buttonLabel}
    </Button>
  )
}

export function StripeErrorProgramBundleCardSettingsButton({
  programBundleId,
  paymentError,
  buttonSize,
}: {
  programBundleId: string
  paymentError: PaymentError
  buttonSize?: ButtonVariants["size"]
}) {
  const { data: programBundle, isLoading } = useProgramBundle(programBundleId)
  const {
    openPaymentSettings,
    isUpdating: isUpdatingSubscription,
    programBundleStripeSubscriptionInfos,
    tryNewPayment,
  } = useProgramBundleAthleteSubscription(programBundle)

  const userSubscriptionError = programBundleStripeSubscriptionInfos?.error
  const buttonLabel = getPaymentErrorButtonLabel(paymentError)

  return (
    <Button
      variant="primary"
      size={buttonSize ?? "md"}
      className="w-full"
      onClick={() => {
        if (userSubscriptionError?.errorCode === StripeErrorCode.AUTHENTICATION_REQUIRED) tryNewPayment()
        else openPaymentSettings()
      }}
      disabled={isLoading}
      isLoading={isUpdatingSubscription}
    >
      {isUpdatingSubscription && <LoaderCircle className="animate-spin" />}
      {buttonLabel}
    </Button>
  )
}

export function StripeErrorMissingDocumentButton({
  accountErrorCode,
  openBankingTab,
  buttonSize,
}: {
  accountErrorCode: StripeAccountErrorCode
  openBankingTab?: boolean
  buttonSize?: ButtonVariants["size"]
}) {
  const { updateStripeAccount, loading } = useStripeAccount()
  const boxNavigate = useBoxNavigate()
  const user = useUser()

  const openStripeAccount = () => {
    if (openBankingTab) {
      boxNavigate(Path.Box.Admin + "?tab=banking")
    } else if (
      user.stripeAccountStatus === StripeAccountStatus.OK ||
      user.stripeAccountStatus === StripeAccountStatus.REQUIRE_ADDITIONAL_INFORMATION ||
      user.stripeAccountStatus === StripeAccountStatus.ONBOARDING
    )
      updateStripeAccount()
    else throw new Error("Stripe account is not created")
  }

  const { primaryButtonLabel } = getStripeAccountErrorText(accountErrorCode)

  return (
    <Button
      variant="primary"
      size={buttonSize ?? "sm"}
      className="w-fit self-center"
      onClick={openStripeAccount}
      disabled={loading}
      isLoading={loading}
    >
      {loading && <LoaderCircle className="animate-spin" />}
      {primaryButtonLabel}
    </Button>
  )
}
